import React, { useState } from 'react';
import SearchableDropdown from './SearchableDropdown';
import './TaskAssigneePicker.css';

export interface TaskAssigneePickerUser {
  id: number;
  username: string;
}

interface TaskAssigneePickerProps {
  users: TaskAssigneePickerUser[];
  value: number[];
  onChange: (userIds: number[]) => void;
  addPlaceholder?: string;
}

/**
 * Multi-select verified users for task assignment (chips + searchable add).
 */
const TaskAssigneePicker: React.FC<TaskAssigneePickerProps> = ({
  users,
  value,
  onChange,
  addPlaceholder = 'Add person…',
}) => {
  const [addField, setAddField] = useState('');

  const selectedUsers = value
    .map((id) => users.find((u) => u.id === id))
    .filter((u): u is TaskAssigneePickerUser => u != null);

  const availableUsernames = users.filter((u) => !value.includes(u.id)).map((u) => u.username);

  const tryAddUsername = (display: string) => {
    const trimmed = display.trim();
    if (!trimmed) return;
    const u = users.find((x) => x.username === trimmed);
    if (u && !value.includes(u.id)) {
      onChange([...value, u.id]);
      setAddField('');
    }
  };

  const remove = (id: number) => {
    onChange(value.filter((x) => x !== id));
  };

  return (
    <div className="task-assignee-picker">
      <div className="task-assignee-picker-chips">
        {selectedUsers.length === 0 ? (
          <span className="task-assignee-picker-empty">No one assigned</span>
        ) : (
          selectedUsers.map((u) => (
            <span key={u.id} className="task-assignee-chip">
              {u.username}
              <button
                type="button"
                className="task-assignee-chip-remove"
                onClick={() => remove(u.id)}
                aria-label={`Remove ${u.username}`}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <SearchableDropdown
        options={availableUsernames}
        value={addField}
        onChange={(v) => {
          setAddField(v);
          tryAddUsername(v);
        }}
        placeholder={availableUsernames.length === 0 ? 'Everyone selected' : addPlaceholder}
      />
    </div>
  );
};

export default TaskAssigneePicker;
